import type { CreateVideoGenTaskPayload, VideoGenConfig, VideoGenTask, VideoGenTaskStatus, VideoGenerationProviderChannel } from '../../../api'
import { filterChannelsByAuth } from './authMatrixRuntime'

/** Canvas video generation provider/channel selection (product layer). */

export const PROVIDER_CHANNEL_LOCAL_LTX = 'LOCAL_LTX'
export const PROVIDER_CHANNEL_MINIMAX = 'MINIMAX'
export const PROVIDER_CHANNEL_LEGACY = 'LEGACY'

/** Advanced Video Generation Card §36 — Skill is a first-class Vistoera
 * asset, same convention as `GenerateImageNode`'s `IMAGE_SKILL_KEY` /
 * `IMAGE_SKILL_VERSION` (AUTO vs one EXPLICIT canonical Skill this
 * round — Skill logic itself, e.g. Shot-context construction/motion-
 * camera synthesis, stays server/Skill-side, never hardcoded here per
 * spec §36). */
export const VIDEO_SKILL_KEY = 'video_generation_v1'
export const VIDEO_SKILL_VERSION = 'v1'

export type { VideoGenConfig, VideoGenerationProviderChannel }

export interface VideoGenerationProviderConfigShape {
  provider_channels?: VideoGenerationProviderChannel[]
}

export function parseProviderChannels(
  config: VideoGenerationProviderConfigShape | VideoGenConfig | null | undefined,
): VideoGenerationProviderChannel[] {
  return Array.isArray(config?.provider_channels) ? config.provider_channels : []
}

/** VistoeraTASK-00634 — only channels the member may use for generate_video. */
export function authFilteredVideoProviderChannels(
  channels: VideoGenerationProviderChannel[],
  nodeType = 'generate_video',
): VideoGenerationProviderChannel[] {
  return filterChannelsByAuth(
    nodeType,
    channels.filter((c) => isVideoProviderChannelExecutionSupported(c)),
  )
}

export function resolveProviderChannel(
  fields: Record<string, unknown>,
  channels: VideoGenerationProviderChannel[],
): VideoGenerationProviderChannel | null {
  const raw = fields.providerChannel as string | undefined
  if (!raw || raw === PROVIDER_CHANNEL_LEGACY) return null
  return channels.find((c) => c.id === raw) ?? null
}

/** Backend-owned execution readiness — never infer credential/adapter state
 * on the client (TASK-00049). Legacy channels without `readiness` stay
 * enabled until the server rejects submit. */
export function isVideoProviderChannelExecutionSupported(
  channel: VideoGenerationProviderChannel | null | undefined,
): boolean {
  if (!channel?.readiness || typeof channel.readiness !== 'object') return true
  const flag = (channel.readiness as { execution_supported?: unknown }).execution_supported
  return flag !== false
}

export function usesExplicitVideoProviderChannel(fields: Record<string, unknown>): boolean {
  const raw = fields.providerChannel as string | undefined
  return !!raw && raw !== PROVIDER_CHANNEL_LEGACY
}

/** New Canvas nodes leave `providerChannel` UNSET (never a hardcoded
 * Provider constant — task spec VISTOERA_GENERATE_VIDEO_CANONICAL_
 * PROVIDER_ID_AND_ADMIN_DEFAULT_PROVIDER_END_TO_END_P0_V1 §P4 hard rule:
 * "禁止继续 const DEFAULT_PROVIDER = LOCAL_LTX"). `GenerateVideoNode`
 * resolves the actual default lazily, from the Platform Admin's CURRENT
 * canonical default (`VideoGenConfig.default_provider_option_id`), the
 * first time the node renders with real config loaded — see
 * `resolveDefaultProviderChannelId` below. This also means an existing
 * node's persisted selection is untouched by a later Admin default
 * change (§P5 Case A/B): once `providerChannel` is set (by that one-time
 * resolution, or by the tenant explicitly picking one), it is never
 * silently re-resolved again.
 *
 * `keyframeTimeline`/`dialogueSegments`/`inputAssetRefs` are deliberately
 * OMITTED here (spec §43 "手工创建 Video Node...默认最简单状态") — an
 * absent array means "0 keyframes / no dialogue / no input assets", the
 * simplest LEVEL 0 (Prompt → Video) capability-ladder rung, never an
 * empty-but-present array forcing the Advanced Card to render 3 blank
 * slots up front. */
export function buildGenerateVideoNodeDefaultFields(): Record<string, unknown> {
  return {
    promptText: '',
    enhancedPromptText: '',
    effectivePromptSource: 'ORIGINAL',
    boundPromptEnhancerNodeId: null,
    promptEnhancementRunId: null,
    promptEnhancementSkillId: null,
    promptEnhancementSkillVersion: null,
    promptEnhancementProviderId: null,
    promptEnhancementUpdatedAt: null,
    ratio: '16:9',
    qualityTier: '1080P',
    durationSeconds: 5,
    status: 'READY',
    skillSelectionSource: 'AUTO',
    skillKey: null,
    skillVersion: null,
  }
}

/** Task spec §P4/§P8 — resolves the Canvas execution-channel id
 * (`fields.providerChannel`'s own value space, e.g. `"LOCAL_LTX"` or
 * `"PROVIDER_OPTION::<uuid>"`) for the Platform Admin's CURRENT canonical
 * default Provider, given the already-loaded shared `VideoGenConfig`.
 * Returns `null` when the config hasn't loaded yet, or when no channel's
 * `provider_option_id` matches `default_provider_option_id` (e.g. no
 * Admin default is currently configured, or it resolves to a
 * disabled/hidden channel) — callers must NEVER fall back to a hardcoded
 * channel id in that case (§P4/§P9 hard rule against silent fallback);
 * leaving `providerChannel` unset until a real default exists is the
 * correct behavior. */
export function resolveDefaultProviderChannelId(
  config: VideoGenerationProviderConfigShape | VideoGenConfig | null | undefined,
  channels: VideoGenerationProviderChannel[],
): string | null {
  const defaultProviderOptionId = (config as VideoGenConfig | null | undefined)?.default_provider_option_id
  if (!defaultProviderOptionId) return null
  const match = channels.find((c) => c.provider_option_id === defaultProviderOptionId)
  return match?.id ?? null
}

/** Advanced Video Generation Card §34 — resolves the
 * `VideoGenEngineCapabilityProfile` lookup key (`capability_matrix`'s
 * own keys, e.g. `"ltx"`/`"minimax"`) for the CURRENTLY selected
 * provider — explicit channel's `route_policy.route_mode` when present,
 * else the legacy engine picker's own value. Returns `null` for
 * AUTO/cloud selections: cloud video models are gated by a SEPARATE,
 * not-yet-frontend-surfaced `CloudVideoModelCapability` registry
 * (`backend/app/saas/cloud_video_providers/model_registry.py`) — this
 * function never guesses a capability profile for those, it just skips
 * the client-side pre-check and lets the server's own authoritative
 * `video_execution_planner` gate apply (spec §30 "不要猜"). */
export function resolveCapabilityEngineKey(
  channel: VideoGenerationProviderChannel | null,
  legacyEngine: string | null | undefined,
): string | null {
  if (channel) {
    const mode = channel.route_policy?.route_mode || channel.route_mode
    if (mode === 'LOCAL_LTX') return 'ltx'
    if (mode === 'LOCAL_MINIMAX') return 'minimax'
    return null
  }
  if (legacyEngine && legacyEngine !== 'auto') return legacyEngine
  return null
}

/**
 * VistoeraTASK-00197 §五 — the ONE canonical place that turns however
 * many ordered reference-image asset-id sources (keyframe-derived
 * visual refs, the legacy scalar fallback, structured Character/
 * Product/Scene/Other-Reference chips, …) into a single, deduplicated,
 * stably-ordered `reference_image_asset_ids` list.
 *
 * Semantics (byte-for-byte the same algorithm `GenerateVideoNode.tsx`'s
 * submit handler already ran inline before this task — extracted here,
 * not rewritten, so it is directly unit-testable and reusable):
 *   - Sources are visited IN ORDER; each source's own ids are visited in
 *     THEIR order.
 *   - `null`/`undefined`/empty-string entries are skipped.
 *   - The FIRST occurrence of a given `asset_id` (across ALL sources)
 *     wins both its POSITION and, if a later duplicate of the same id
 *     also appears in an EARLIER-visited source's list, that earlier
 *     source's id also wins — i.e. dedupe-by-first-seen, never last-
 *     seen (VistoeraTASK-00195's real-Job fix depends on this: a
 *     spurious duplicate 4th `reference_image`-role id must collapse
 *     into whichever of the 3 real character/scene ids it duplicates,
 *     never survive as an extra 4th canonical id).
 *
 * Explicitly does NOT resolve Canvas semantic role (`canvas_role`) —
 * that is a separate concern (`reference_image_bindings`, spec §四/§八)
 * with its own per-caller role-source lookup; merging role resolution
 * into this generic dedupe utility would risk conflating two
 * non-substitutable contract fields, which is exactly the anti-pattern
 * this task exists to undo.
 */
export function mergeCanonicalReferenceImageAssetIds(
  ...sources: Array<Array<string | null | undefined> | string | null | undefined>
): string[] {
  const merged: string[] = []
  const seen = new Set<string>()
  const push = (rawId: string | null | undefined) => {
    if (!rawId || seen.has(rawId)) return
    seen.add(rawId)
    merged.push(rawId)
  }
  for (const source of sources) {
    if (Array.isArray(source)) {
      for (const id of source) push(id)
    } else {
      push(source)
    }
  }
  return merged
}

export function buildVideoGenerationSubmitPayload(args: {
  channel: VideoGenerationProviderChannel | null
  prompt: string
  aspect_ratio: string
  quality_tier: string
  duration_seconds: number
  seed_mode: 'random' | 'fixed'
  enhance_prompt: boolean
  reference_image_asset_id?: string | null
  /** VistoeraTASK-00157 §九/§十四 (originated) + VistoeraTASK-00181
   * (re-affirmed the same field name — VistoeraTASK-00197 merged the two
   * accidental duplicate declarations back into this single canonical
   * one) — the canonical, deduplicated array of ALL structured reference
   * image asset ids (character/product/scene/other-reference) that are
   * ready to submit, already-materialized `VideoGenerationAsset` ids
   * (see `GenerateVideoNode.tsx`'s submit handler). Backend dedups by
   * `asset_id` against the singular/keyframe fields — safe to pass
   * overlapping ids. */
  reference_image_asset_ids?: string[] | null
  /** VistoeraTASK-00191 — per-canonical-image Canvas semantic role
   * mapping (Picture N -> Subject/Scene/…), SAME array length/order as
   * `reference_image_asset_ids` above. A separate field, never a
   * substitute for it. */
  reference_image_bindings?: Array<{ asset_id: string; canvas_role: string; ordinal: number }> | null
  reference_video_asset_id?: string | null
  reference_video_asset_ids?: string[] | null
  /** VistoeraTASK-00150 §四/§六 — already-materialized reference audio
   * asset ids (real `VideoGenerationAsset` ids, never a raw Canvas
   * `SiteAsset`/entity id — see `materializeAudioSiteAssetRef` in
   * `GenerateVideoNode.tsx`). */
  reference_audio_asset_ids?: string[] | null
  keyframes?: CreateVideoGenTaskPayload['keyframes']
  source_context?: CreateVideoGenTaskPayload['source_context']
  /** Legacy planner path only — omitted when explicit route_policy is sent. */
  engine?: string | null
  /** Task spec §P5/§P6 Case 4 — explicit in-channel model/mode override
   * (from `channel.model_choices`), when the tenant picked a NON-default
   * model within an already-selected Provider channel. Falls back to the
   * channel's own `route_policy.model_preference`/`model_id` when unset
   * or when it doesn't belong to this channel's own `model_choices` (a
   * stale override left over from a since-changed Provider selection
   * must never leak into a different Provider's routing). */
  modelIdOverride?: string | null
}): CreateVideoGenTaskPayload {
  const payload: CreateVideoGenTaskPayload = {
    prompt: args.prompt,
    aspect_ratio: args.aspect_ratio,
    quality_tier: args.quality_tier,
    duration_seconds: args.duration_seconds,
    seed_mode: args.seed_mode,
    enhance_prompt: args.enhance_prompt,
    reference_image_asset_id: args.reference_image_asset_id ?? null,
    reference_image_asset_ids: args.reference_image_asset_ids ?? null,
    reference_image_bindings: args.reference_image_bindings ?? null,
    reference_video_asset_id: args.reference_video_asset_id ?? null,
    reference_video_asset_ids: args.reference_video_asset_ids ?? null,
    reference_audio_asset_ids: args.reference_audio_asset_ids ?? null,
    keyframes: args.keyframes,
    source_context: args.source_context,
  }
  if (args.channel?.route_policy) {
    const validOverride =
      args.modelIdOverride && args.channel.model_choices?.some((m) => m.id === args.modelIdOverride)
        ? args.modelIdOverride
        : undefined
    payload.route_policy = {
      route_mode: args.channel.route_policy.route_mode ?? undefined,
      provider_preference: args.channel.route_policy.provider_preference ?? undefined,
      // Task spec §P6 — canonical Provider identity, so the server can
      // reject a stale/tampered `provider_id`↔`channel_id` mismatch
      // (`PROVIDER_CHANNEL_MISMATCH`) instead of trusting `route_mode`/
      // `provider_preference` alone.
      provider_option_id: args.channel.provider_option_id ?? args.channel.route_policy.provider_option_id ?? undefined,
      model_preference: validOverride ?? args.channel.route_policy.model_preference ?? args.channel.model_id,
      cloud_fallback_allowed: args.channel.route_policy.cloud_fallback_allowed ?? false,
    }
    return payload
  }
  if (args.engine) {
    payload.engine = args.engine
  }
  return payload
}

export function channelDurationChoices(
  channel: VideoGenerationProviderChannel | null,
  fallback: number[],
  qualityTier?: string,
): number[] {
  const byQuality = channel?.duration_capability?.choices_by_quality_tier
  if (byQuality && qualityTier && byQuality[qualityTier]?.length) {
    return byQuality[qualityTier]
  }
  if (channel?.duration_seconds_choices?.length) return channel.duration_seconds_choices
  return fallback
}

export function channelRatioChoices(channel: VideoGenerationProviderChannel | null, fallback: string[]): string[] {
  if (channel?.supported_ratios?.length) return channel.supported_ratios
  return fallback
}

export function channelQualityChoices(
  channel: VideoGenerationProviderChannel | null,
  fallback: { value: string; label: string }[],
): { value: string; label: string }[] {
  if (channel?.quality_tier_choices?.length) return channel.quality_tier_choices
  return fallback
}

/** VistoeraTASK-00143 §八/§九 — Provider-aware audio-INPUT capability
 * check. Reads the SAME generic per-channel `parameter_support` map
 * every other capability flag already uses (`ratio`/`duration`/
 * `resolution`/`native_audio` — see `video_generation_provider_config.py`),
 * never a hardcoded Provider-name branch (`if provider === 'seedance_2_5'`
 * is explicitly forbidden by spec §八). `undefined`/unknown reads as
 * UNSUPPORTED — a Provider must explicitly declare `"SUPPORTED"` before
 * Canvas ever treats a connected audio reference as consumable, never
 * the reverse "assume supported unless told otherwise" default (spec §九
 * "禁止...假装已经把 audio 发给 Provider"). `null` (no explicit channel
 * selected yet / legacy engine picker) is also UNSUPPORTED — the
 * legacy/local engines never implement an audio-input adapter today. */
export function channelSupportsAudioInput(channel: VideoGenerationProviderChannel | null | undefined): boolean {
  return channel?.parameter_support?.audio_input === 'SUPPORTED'
}

export function taskStatusToCanvasFieldStatus(status: VideoGenTaskStatus): string {
  if (status === 'succeeded') return 'COMPLETED'
  if (status === 'failed') return 'FAILED'
  if (status === 'running') return 'GENERATING'
  return 'QUEUED'
}

/** Map API task → Canvas node `fields.status` (includes WAITING_FOR_ENGINE). */
export function resolveCanvasVideoTaskFieldStatus(task: VideoGenTask): string {
  if (task.waiting_for_engine || task.engine_activation?.waiting_for_engine) {
    return 'WAITING_FOR_ENGINE'
  }
  return taskStatusToCanvasFieldStatus(task.status)
}
